import { useEffect, useMemo, useRef, useState } from "react";
import { quoteWholesaleCart, placeWholesaleOrder } from "../services/wholesaleService";

export const money = (v, currency = "USD") => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  try { return n.toLocaleString("en-US", { style: "currency", currency }); } catch { return `$${n.toFixed(2)}`; }
};
export const variantLabel = (filter) => Object.entries(filter || {}).map(([k, v]) => `${k}: ${v}`).join(" · ");

// qty is { [variantIndex]: n } for products with variants, { "": n } otherwise.
export const buildItems = (product, qty) => {
  if (!product?.variants?.length) return qty[""] > 0 ? [{ providerProductId: product._id, quantity: qty[""] }] : [];
  return product.variants.map((v, i) => ({ providerProductId: product._id, variant: v.filter, quantity: qty[i] || 0 })).filter((it) => it.quantity > 0);
};

// Next tier the Seller could reach (display hint only -- the server prices the order).
export const nextTierHint = (product, totalUnits) => {
  const next = [...(product?.quantityPricing || [])].sort((a, b) => a.minQuantity - b.minQuantity).find((t) => t.minQuantity > totalUnits);
  if (!next) return null;
  const unit = next.unitPrice != null ? next.unitPrice : (Number(product.basePrice) || 0) * (1 - (next.discountPercent || 0) / 100);
  return { missing: next.minQuantity - totalUnits, minQuantity: next.minQuantity, unit };
};

// Wholesale purchase state for one Provider product: quantity distribution, live SERVER quote
// (stale answers dropped), and order placement.
export default function useWholesaleCart(product, token) {
  const [qty, setQty] = useState({});
  const [delivery, setDelivery] = useState("");
  const [note, setNote] = useState("");
  const [address, setAddress] = useState(null);      // adresse de livraison CHOISIE par le Seller (jamais déduite)
  const keyRef = useRef(null);                        // une clé d'idempotence par tentative d'achat (renvoyée telle quelle au retry)
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const items = useMemo(() => (product ? buildItems(product, qty) : []), [product, qty]);
  const itemsKey = JSON.stringify(items);
  const totalUnits = items.reduce((s, i) => s + i.quantity, 0);

  useEffect(() => {
    if (!product || !items.length) { setQuote(null); setQuoteError(null); return undefined; }
    const mine = ++seq.current;
    const timer = setTimeout(async () => {
      const r = await quoteWholesaleCart(token, items, delivery || undefined);
      if (mine !== seq.current) return;
      if (r?.success === false || r?.error) { setQuote(null); setQuoteError(r); } else { setQuote(r); setQuoteError(null); }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, delivery, product, token]);

  const setQ = (key, value, max) => setQty((q) => ({ ...q, [key]: Math.max(0, Math.min(max, Math.floor(Number(value) || 0))) }));
  const place = async () => {
    setBusy(true);
    if (!keyRef.current) keyRef.current = (window.crypto?.randomUUID?.() || `k-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const r = await placeWholesaleOrder(token, { items, deliveryOption: delivery, note, shippingAddress: address || undefined, idempotencyKey: keyRef.current });
    setBusy(false);
    if (r?.success === false || r?.error) { setQuoteError(r); return null; }
    keyRef.current = null;
    return r.order;
  };
  return { qty, setQty, setQ, delivery, setDelivery, note, setNote, address, setAddress, resetAttempt: () => { keyRef.current = null; }, quote, quoteError, items, totalUnits, busy, place };
}
